import type { VerwaltungAuftrag } from '@nac2s/shared'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Knopf } from '@/teile/Knopf'
import { Status, type Ton } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { auftragEntscheiden, ladeAuftraege } from '../api/verwaltung.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { zeitpunkt } from '../teile/monat.js'

/**
 * Aufträge aller Partner — live aus den Stacks geholt, nicht gespeichert.
 * Freigeben oder Zurückziehen geht an den Stack; die Entscheidung liegt dort.
 */

type Ladezustand =
  | { status: 'laedt' }
  | {
      status: 'da'
      auftraege: VerwaltungAuftrag[]
      nichtErreichbar: Array<{ partnerId: string; partnerName: string }>
    }
  | { status: 'fehler'; meldung: string }

export function Auftraege() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const antwort = await ladeAuftraege()
      setZustand({ status: 'da', ...antwort })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  async function entscheiden(a: VerwaltungAuftrag, entscheidung: 'freigeben' | 'loeschen') {
    setLaeuft(a.id)
    setMeldung(null)
    try {
      await auftragEntscheiden(a.partnerId, a.id, entscheidung)
      await laden()
    } catch (fehler: unknown) {
      setMeldung(fehler instanceof ApiError ? fehler.message : 'Die Entscheidung kam nicht an.')
    } finally {
      setLaeuft(null)
    }
  }

  const offen =
    zustand.status === 'da'
      ? zustand.auftraege.filter((a) => a.zustand === 'wartet_auf_freigabe')
      : []
  const uebrige =
    zustand.status === 'da'
      ? zustand.auftraege.filter((a) => a.zustand !== 'wartet_auf_freigabe')
      : []

  return (
    <div>
      <Seitenkopf
        beisatz="Alle Partner"
        titel="Aufträge"
        satz="Bereitstellungen, die auf netable warten — und was die Stacks sonst gerade tun."
        rechts={<Knopf onClick={() => void laden()}>Neu laden</Knopf>}
      />

      <div className="flex flex-col gap-6 px-6 pb-8">
        {zustand.status === 'da' && zustand.nichtErreichbar.length > 0 ? (
          <Fehler>
            Nicht erreichbar: {zustand.nichtErreichbar.map((p) => p.partnerName).join(', ')}. Deren
            Aufträge fehlen in dieser Liste.
          </Fehler>
        ) : null}
        {meldung ? <Fehler>{meldung}</Fehler> : null}

        {zustand.status === 'laedt' ? (
          <Laedt>Aufträge werden bei den Partnern abgefragt …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : (
          <>
            <section>
              <h2 className="mb-2 text-[13px] font-semibold text-primary">
                Wartet auf Freigabe{' '}
                <span className="font-normal text-tertiary tabular-nums">({offen.length})</span>
              </h2>
              {offen.length === 0 ? (
                <Leer titel="Nichts wartet">
                  Kein Partner hat gerade einen Auftrag, den netable freigeben müsste.
                </Leer>
              ) : (
                <Tabelle kopf={['Partner', 'Kunde', 'Art', 'Beantragt', 'Von', '']}>
                  {offen.map((a) => (
                    <Zeile key={`${a.partnerId}:${a.id}`} a={a}>
                      <div className="flex justify-end gap-2">
                        <Knopf
                          gefahr
                          disabled={laeuft === a.id}
                          onClick={() => void entscheiden(a, 'loeschen')}
                        >
                          Zurückziehen
                        </Knopf>
                        <Knopf
                          stark
                          disabled={laeuft === a.id}
                          onClick={() => void entscheiden(a, 'freigeben')}
                        >
                          Freigeben
                        </Knopf>
                      </div>
                    </Zeile>
                  ))}
                </Tabelle>
              )}
            </section>

            {uebrige.length > 0 ? (
              <section>
                <h2 className="mb-2 text-[13px] font-semibold text-primary">
                  Übrige Aufträge{' '}
                  <span className="font-normal text-tertiary tabular-nums">({uebrige.length})</span>
                </h2>
                <Tabelle kopf={['Partner', 'Kunde', 'Art', 'Beantragt', 'Von', 'Zustand']}>
                  {uebrige.map((a) => (
                    <Zeile key={`${a.partnerId}:${a.id}`} a={a}>
                      <span className="flex flex-col items-end gap-1">
                        <Zustandsmarke a={a} />
                        {a.fehler ? (
                          <span className="max-w-[16rem] truncate text-[11.5px] text-tertiary">
                            {a.fehler}
                          </span>
                        ) : null}
                      </span>
                    </Zeile>
                  ))}
                </Tabelle>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function Zeile({ a, children }: { a: VerwaltungAuftrag; children: React.ReactNode }) {
  return (
    <tr className="border-b border-secondary last:border-b-0">
      <td className="py-2.5 pr-2.5">
        <Link to={`/partner/${a.partnerId}`} className="font-medium text-primary hover:underline">
          {a.partnerName}
        </Link>
      </td>
      <td className="py-2.5 pr-2.5">
        <span className="block text-primary">{a.anzeigename}</span>
        <span className="block text-[11.5px] text-quaternary">
          {a.host}
          {a.edition ? ` · ${a.edition === 'pro' ? 'Pro' : 'Standard'}` : ''}
          {a.seatCap !== null ? ` · bis ${a.seatCap} Seeds` : ''}
          {a.istDemo ? ' · Demo' : ''}
        </span>
      </td>
      <td className="py-2.5 pr-2.5 text-tertiary">{ART[a.art] ?? a.art}</td>
      <td className="py-2.5 pr-2.5 text-tertiary tabular-nums">{zeitpunkt(a.beantragtAm)}</td>
      <td className="py-2.5 pr-2.5 text-tertiary">{a.beantragtVon ?? '—'}</td>
      <td className="py-2.5 text-right">{children}</td>
    </tr>
  )
}

const ART: Record<string, string> = {
  bereitstellen: 'Bereitstellen',
  deaktivieren: 'Deaktivieren',
  loeschen: 'Löschen',
}

const ZUSTAND: Record<VerwaltungAuftrag['zustand'], { ton: Ton; text: string }> = {
  wartet_auf_freigabe: { ton: 'wartet', text: 'wartet auf Freigabe' },
  freigegeben: { ton: 'wartet', text: 'freigegeben' },
  laeuft: { ton: 'wartet', text: 'läuft' },
  fertig: { ton: 'gut', text: 'fertig' },
  fehlgeschlagen: { ton: 'stoerung', text: 'fehlgeschlagen' },
  zurueckgezogen: { ton: 'ruhig', text: 'zurückgezogen' },
}

export function Zustandsmarke({ a }: { a: Pick<VerwaltungAuftrag, 'zustand'> }) {
  const z = ZUSTAND[a.zustand]
  return <Status ton={z.ton}>{z.text}</Status>
}
